// src/components/dashboards/add_patient_v2/hooks/useExternalIdMatch.ts
// -----------------------------------------------------------------------------
// Hook for the V2 form's external_id duplicate probe. Extracted from
// AddMoreDetailsPanel so PatientCard can drive merge-mode independently of
// whether the user expanded the "more details" panel. The hook owns:
//   - debounced GET /patients/check-external-id/
//   - matched-patient lift via callback (so the parent card's state knows
//     when the form should switch into "Attach to <name>" mode)
//   - cancellation on rapid keystrokes so a slow probe never overwrites a
//     newer match result
// See cursor_logs/036 §16.
// -----------------------------------------------------------------------------

import { useEffect, useRef, useState } from "react";
import api from "../../../../api/api";
import logger from "utils/logger";

export interface DuplicateMatch {
  id: number;
  name: string;
  external_id: string;
  token: string;
}

export interface ExternalIdOption {
  external_id: string;
  patient_name: string;
  patient_id: number;
  /** The option patient's environments within the user's visible scope
   *  (cursor_logs/047). Lets the card detect a pick that lands in an
   *  UNticked env and offer "Activate <env>", symmetric with the name picker.
   *  May be absent for older backends. */
  environment_ids?: string[];
  environment_names?: string[];
}

export interface UseExternalIdMatchArgs {
  externalId: string;
  environmentId?: string;
  /** All ticked environments (cursor_logs/046). The duplicate probe and the
   *  suggestion dropdown cover the batch's WHOLE target world — scoping to
   *  the primary alone hid same-token patients living in a ticked sibling
   *  env. environmentId stays the primary. */
  environmentIds?: string[];
  /** When false the hook resets state and skips the probe (e.g. card is
   *  already in a succeeded state and we don't want to re-probe). */
  enabled?: boolean;
  /** Optional second key driving the existing-IDs autocomplete dropdown. If
   *  unset the hook reuses `externalId` for both the merge probe and the
   *  list query — which is fine when both keys mean the same thing. The
   *  multi-tag chip input passes the user's current DRAFT here so the
   *  suggestion list filters on what the user is typing right now, while the
   *  merge probe keeps using the full comma-joined committed value (which
   *  the backend tokenises). See cursor_logs/036 §16. */
  listQuery?: string;
  /** Called whenever the first match changes (including back to null when
   *  the external_id is erased). */
  onMatchedPatientChange?: (
    match: { id: number; name: string; external_id: string } | null,
  ) => void;
}

export interface UseExternalIdMatchResult {
  matches: DuplicateMatch[];
  checking: boolean;
  /** Server-filtered list of existing IDs in the environment matching the
   *  current input. Drives the autocomplete dropdown in PatientCard's
   *  header. */
  existingIds: ExternalIdOption[];
  /**
   * Durable signal of WHICH external_id string the current `matches` array
   * corresponds to. This is the state-driven replacement for the §27
   * timer-based submit gate (cursor_logs/036 §28): the consumer can compare
   * `resolvedFor` to its committed external_id to know, with no timer race,
   * whether the collision probe for that exact value has resolved.
   *   - the trimmed external_id when a probe SUCCEEDED (or errored non-cancel,
   *     so the gate never hangs — backend dedup is the safety net),
   *   - "" when the external_id is empty (nothing to check → resolved),
   *   - null when the hook is disabled / has no environment to probe against.
   */
  resolvedFor: string | null;
}

export function useExternalIdMatch({
  externalId,
  environmentId,
  environmentIds,
  enabled = true,
  listQuery,
  onMatchedPatientChange,
}: UseExternalIdMatchArgs): UseExternalIdMatchResult {
  // Stable comma-joined key for the ticked envs so effects don't re-fire on
  // array identity churn (cursor_logs/046).
  const environmentIdsKey =
    environmentIds && environmentIds.length
      ? environmentIds.map((id) => String(id)).join(",")
      : "";
  const [matches, setMatches] = useState<DuplicateMatch[]>([]);
  const [checking, setChecking] = useState(false);
  const [existingIds, setExistingIds] = useState<ExternalIdOption[]>([]);
  // See UseExternalIdMatchResult.resolvedFor — the durable submit gate.
  const [resolvedFor, setResolvedFor] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const listTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const listAbortRef = useRef<AbortController | null>(null);
  const lastMatchKey = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !environmentId) {
      setMatches([]);
      setChecking(false);
      // No environment to probe against → the gate cannot resolve here.
      // PatientCard treats a missing environmentId as "don't block".
      setResolvedFor(null);
      return;
    }
    const trimmed = externalId.trim();
    if (timerRef.current) clearTimeout(timerRef.current);
    if (abortRef.current) abortRef.current.abort();
    if (!trimmed) {
      setMatches([]);
      setChecking(false);
      // Empty value is, by definition, resolved — there is nothing to check.
      setResolvedFor("");
      return;
    }
    setChecking(true);
    timerRef.current = setTimeout(async () => {
      try {
        const controller = new AbortController();
        abortRef.current = controller;
        const resp = await api.get("/patients/check-external-id/", {
          params: {
            environment_id: environmentId,
            // Whole batch scope (cursor_logs/046); backend validates each id.
            ...(environmentIdsKey ? { environment_ids: environmentIdsKey } : {}),
            external_id: trimmed,
          },
          signal: controller.signal,
        });
        const data = resp.data as { exists: boolean; matches: DuplicateMatch[] };
        setMatches(data.matches || []);
        // The matches now correspond to `trimmed` — mark the gate resolved.
        setResolvedFor(trimmed);
      } catch (err: unknown) {
        const e = err as { name?: string; code?: string };
        if (e?.name !== "CanceledError" && e?.code !== "ERR_CANCELED") {
          logger.debug("[useExternalIdMatch] probe failed", err);
          // A real (non-cancel) error must NOT hang the submit gate — resolve
          // it anyway and let the backend dedup be the ultimate safety net.
          setResolvedFor(trimmed);
        }
        // On a CanceledError a newer probe is already in flight; leave
        // resolvedFor untouched so the gate waits for that probe.
      } finally {
        setChecking(false);
      }
    }, 400);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [externalId, environmentId, environmentIdsKey, enabled]);

  // Lift the resolved first-match to the parent. Use a stable key so we
  // don't double-fire when matches are the same patient between probes.
  useEffect(() => {
    if (!onMatchedPatientChange) return;
    const first = matches[0];
    const key = first ? `${first.id}::${first.token}` : null;
    if (key === lastMatchKey.current) return;
    lastMatchKey.current = key;
    onMatchedPatientChange(
      first
        ? { id: first.id, name: first.name, external_id: first.external_id }
        : null,
    );
  }, [matches, onMatchedPatientChange]);

  // Server-filtered autocomplete options. Separate debounce from the
  // duplicate-match probe because the list endpoint returns a different
  // shape and we want it even when nothing has matched yet (e.g. user is
  // browsing existing IDs).
  // Use the explicit listQuery when provided (multi-tag chip input passes
  // the draft string), otherwise fall back to externalId.
  const effectiveListQuery = listQuery ?? externalId;
  useEffect(() => {
    if (!enabled || !environmentId) {
      setExistingIds([]);
      return;
    }
    const trimmed = effectiveListQuery.trim();
    if (listTimerRef.current) clearTimeout(listTimerRef.current);
    if (listAbortRef.current) listAbortRef.current.abort();
    if (!trimmed) {
      setExistingIds([]);
      return;
    }
    listTimerRef.current = setTimeout(async () => {
      try {
        const controller = new AbortController();
        listAbortRef.current = controller;
        const resp = await api.get("/patients/list-external-ids/", {
          params: {
            environment_id: environmentId,
            // Whole batch scope (cursor_logs/046).
            ...(environmentIdsKey ? { environment_ids: environmentIdsKey } : {}),
            q: trimmed,
          },
          signal: controller.signal,
        });
        const data = resp.data as { items: ExternalIdOption[] };
        setExistingIds(data.items || []);
      } catch (err: unknown) {
        const e = err as { name?: string; code?: string };
        if (e?.name !== "CanceledError" && e?.code !== "ERR_CANCELED") {
          logger.debug("[useExternalIdMatch] list failed", err);
        }
      }
    }, 250);
    return () => {
      if (listTimerRef.current) clearTimeout(listTimerRef.current);
    };
  }, [effectiveListQuery, environmentId, environmentIdsKey, enabled]);

  return { matches, checking, existingIds, resolvedFor };
}

export default useExternalIdMatch;
